import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import LandingArticle from '@/components/LandingArticle';

/**
 * Pillar page targeting the exact-match commercial keyword "BCM-95®".
 *
 * Strategy: dense first-100-word keyword presence, multiple H2 sections
 * answering "What is BCM-95®?", "Where to buy BCM-95® UK?",
 * "BCM-95® vs curcumin", "BCM-95® dosage", plus internal links
 * to the existing /bcm-95-explained and /curcumin-vs-turmeric pillars
 * (semantic clustering) and breadcrumb up to homepage.
 *
 * Strict MHRA/ASA compliance — no medical or curative claims.
 */
const InlineCTA = () => (
  <div className="my-6 not-prose rounded-2xl border border-emerald-700/15 bg-emerald-50/60 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="inline-cta">
    <div className="text-sm text-emerald-900">
      <strong>Buy authentic BCM-95® in the UK.</strong>
      <span className="block text-emerald-800/80 mt-0.5">60 vegetarian capsules &middot; FREE Royal Mail 48 &middot; first-bottle welcome price &pound;37.99 all-in.</span>
    </div>
    <Link to="/products/94056720-f372-4eef-bcfe-74063137a6e5" className="inline-flex shrink-0 items-center gap-1.5 bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-full text-sm hover:bg-emerald-800 transition-colors" data-testid="biocurcumax-pillar-cta">
      Shop BCM-95® <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </div>
);

const sections = [
  {
    id: 'what-is-biocurcumax',
    heading: 'What is BCM-95®?',
    body: (
      <>
        <p>
          <strong>BCM-95®</strong> is a high&#8209;bioavailability curcumin food supplement
          built around the patented <strong>BCM&#8209;95<sup>&reg;</sup></strong> (also marketed internationally
          as <em>Curcugreen<sup>&reg;</sup></em>) formulation. It is distributed and marketed in the United Kingdom by
          IIY Phytoventra Ltd from our Northampton base, with FREE Royal Mail 48 delivery and
          all&#8209;inclusive UK VAT pricing &mdash; no hidden fees at checkout.
        </p>
        <p>
          The product itself is a single&#8209;plant extract from <em>Curcuma longa</em> (turmeric)
          rhizome, standardised to ~95% curcuminoids and reintegrated with turmeric&apos;s own
          essential&#8209;oil fraction (the natural turmerones). Because the oily fraction comes from
          the same root as the curcuminoids, BCM-95® delivers BCM&#8209;95 <strong>without piperine</strong>,
          without phospholipid carriers, and without synthetic emulsifiers &mdash; useful for anyone
          looking for a clean&#8209;label, single&#8209;plant curcumin source.
        </p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'why-biocurcumax-is-different',
    heading: 'Why BCM-95® differs from ordinary curcumin',
    body: (
      <>
        <p>
          Plain &quot;95% curcumin extract&quot; capsules are highly potent <em>in vitro</em> but
          poorly absorbed <em>in vivo</em>. The body excretes them rapidly. BCM-95® solves this
          by recombining the standardised curcuminoid concentrate with the natural turmeric oils
          that occur in the whole rhizome &mdash; reproducing, in a controlled and analytically
          standardised way, something closer to the whole&#8209;root chemistry. The reference paper,
          Antony B et al. (Indian J Pharm Sci 2008), reports substantially higher relative oral
          bioavailability for BCM&#8209;95 vs unformulated curcumin in healthy volunteers.
        </p>
        <p>
          In short, three things make BCM-95® stand apart from a generic turmeric capsule:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Patented BCM&#8209;95<sup>&reg;</sup> / Curcugreen<sup>&reg;</sup> formulation</strong> &mdash; published pharmacokinetic data, repeatable extraction.</li>
          <li><strong>No piperine</strong> &mdash; cleaner option for people worried about CYP3A4 drug&#8209;metabolism interactions.</li>
          <li><strong>UK&#8209;regulated food supplement</strong> &mdash; MHRA/ASA&#8209;compliant labelling, full UK company transparency.</li>
        </ul>
        <p>
          We have written a dedicated, plain&#8209;English explainer of the patent and the chemistry:
          read <Link to="/bcm-95-explained" className="text-emerald-700 underline">BCM&#8209;95<sup>&reg;</sup> Explained &mdash; The Patented Curcumin Formulation Behind BCM-95®</Link>.
          Wondering how it differs from a turmeric latte? See <Link to="/curcumin-vs-turmeric" className="text-emerald-700 underline">Curcumin vs Turmeric: What&apos;s actually in your supplement?</Link>
        </p>
      </>
    ),
  },
  {
    id: 'whats-in-a-bottle',
    heading: 'What\u2019s in a bottle of BCM-95®',
    body: (
      <>
        <p>Each IIY Phytoventra BCM-95® bottle contains:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>60 vegetarian capsules</strong> &mdash; pullulan shell, fully vegan&#8209;suitable.</li>
          <li><strong>650 mg of patented BCM&#8209;95<sup>&reg;</sup> curcumin complex</strong> per capsule.</li>
          <li>Standardised to a minimum 86&ndash;95% curcuminoids + reintegrated turmeric essential oils.</li>
          <li><strong>No</strong> piperine, no soy, no gluten, no dairy, no synthetic dyes, no nano&#8209;particles.</li>
          <li>Manufactured by Arjuna Natural in a solar&#8209;powered facility certified to ISO 9001, ISO 14001 and ISO 22000.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-to-take',
    heading: 'How to take BCM-95®',
    body: (
      <>
        <p>
          <strong>One capsule a day, with food.</strong> Morning or evening &mdash; whichever you
          remember most reliably. Curcumin is a fat&#8209;soluble compound, so taking it alongside
          a meal that includes a small amount of fat helps the body absorb it.
        </p>
        <p>
          As with any food supplement, BCM-95® is intended to <em>complement</em> a varied,
          balanced diet and a healthy lifestyle &mdash; it is not a substitute for either. Consult your
          GP or pharmacist if you are pregnant, breastfeeding, on prescription medication, or
          managing a medical condition.
        </p>
        <InlineCTA />
      </>
    ),
  },
  {
    id: 'where-to-buy',
    heading: 'Where to buy BCM-95® in the UK',
    body: (
      <>
        <p>
          BCM-95® is sold direct from <strong>IIY Phytoventra</strong>, its official UK distributor, at <strong>iiypv.com</strong>. Buying
          direct gives you three things online marketplaces typically can&apos;t guarantee:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Batch traceability</strong> &mdash; every order is tied to a specific manufacturing batch with a sequential UK proforma PDF invoice.</li>
          <li><strong>All&#8209;inclusive pricing</strong> &mdash; UK VAT and Royal Mail 48 delivery are included; the price you see is the price you pay.</li>
          <li><strong>First&#8209;time buyer welcome offer</strong> &mdash; &pound;37.99 for your first bottle, no code required (offer ends 30 June 2026).</li>
        </ul>
        <p>VAT&#8209;registered businesses can request an itemised VAT invoice at checkout. Wholesale (101+ bottles) and bespoke wholesale quotes (301+) are handled through our private quote portal.</p>
      </>
    ),
  },
  {
    id: 'compliance',
    heading: 'UK regulatory & quality compliance',
    body: (
      <>
        <p>
          BCM-95® is sold in the United Kingdom by <strong>IIY Phytoventra Ltd</strong>, a UK
          limited company registered in England &amp; Wales. The product is labelled and marketed
          strictly within the boundaries of the UK Food Information Regulations 2014, the EU
          Nutrition &amp; Health Claims Regulation (EC) 1924/2006 as retained in UK law, and the
          Advertising Standards Authority CAP code. We do not make any medical, curative or
          disease&#8209;treatment claims about curcumin.
        </p>
        <p>The brand operates the consumer site <strong>iiypv.com</strong>, an editorial blog at <strong>blog.iiypv.com</strong>, and a private wholesale portal. UK customer service and any quality concerns are routed to <a href="mailto:sales@iiypv.com" className="text-emerald-700 underline">sales@iiypv.com</a>.</p>
      </>
    ),
  },
];

const faqEntities = [
  {
    q: 'What is BCM-95®?',
    a: 'BCM-95®\u00ae is a UK food supplement sold by IIY Phytoventra Ltd built around the patented BCM\u201195\u00ae (Curcugreen\u00ae) curcumin formulation. It pairs standardised curcuminoids with turmeric\u2019s own essential oils to support absorption, and contains no piperine.',
  },
  {
    q: 'Is BCM-95® the same as BCM-95®?',
    a: 'BCM\u201195\u00ae is the patented curcumin formulation. BCM-95®\u00ae is the IIY Phytoventra branded consumer product that delivers BCM\u201195 in a 650\u00a0mg vegetarian capsule, 60 capsules per bottle, distributed in the UK by IIY Phytoventra.',
  },
  {
    q: 'How do I take BCM-95®?',
    a: 'One vegetarian capsule per day, taken with food. Curcumin is fat\u2011soluble, so taking it alongside a meal with a small amount of fat is preferred.',
  },
  {
    q: 'Where can I buy BCM-95® in the UK?',
    a: 'Direct from IIY Phytoventra, the official UK distributor, at iiypv.com with FREE Royal Mail 48 delivery and all\u2011inclusive UK VAT pricing. First\u2011time buyers pay \u00a337.99 for a single bottle until 30 June 2026.',
  },
  {
    q: 'Does BCM-95® contain piperine?',
    a: 'No. BCM-95® is piperine\u2011free. The absorption profile of BCM\u201195 comes from reintegrating turmeric\u2019s own essential\u2011oil fraction with the curcuminoids \u2014 a single\u2011plant approach.',
  },
];

export default function BiocurcumaxGuide() {
  return (
    <>
      {/* Dedicated FAQPage JSON-LD so Google can build a BCM-95® rich result */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqEntities.map(({ q, a }) => ({
              '@type': 'Question',
              name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          }),
        }}
      />
      <LandingArticle
        title={"BCM-95® UK | What It Is, How To Take It, Where To Buy"}
        description={"BCM-95® is the IIY Phytoventra UK curcumin food supplement built on patented BCM-95®. Learn what's inside, how to take it, and where to buy in the UK."}
        pathOverride="/biocurcumax"
        heading={"BCM-95®: The UK Curcumin Food Supplement Guide"}
        subheading={"Everything you need to know about BCM-95® — the patented BCM-95® / Curcugreen® curcumin formulation, distributed and marketed in the UK by IIY Phytoventra."}
        tag="BCM-95® Guide"
        datePublished="2026-06-05"
        sections={sections}
      />
    </>
  );
}
